import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Table as TableIcon } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { isBlockVariable } from '../templateVariableUtils.ts';
import { templateVariableDisplayNames } from '../templateVariableDisplayNames.ts';
import { useSuggestionStore } from '../suggestionStore.ts';

const POPUP_MARGIN = 8;

const SuggestionPopup = () => {
  const open = useSuggestionStore((s) => s.open);
  const items = useSuggestionStore((s) => s.items);
  const clientRect = useSuggestionStore((s) => s.clientRect);
  const selectedIndex = useSuggestionStore((s) => s.selectedIndex);
  const setSelectedIndex = useSuggestionStore((s) => s.setSelectedIndex);
  const select = useSuggestionStore((s) => s.select);
  const hide = useSuggestionStore((s) => s.hide);

  // Dismiss on outside click — TipTap suggestion plugin handles inside-editor
  // clicks itself, this only catches clicks elsewhere on the page.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-suggestion-popup]')) hide();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, hide]);

  if (!open || !clientRect) return null;

  return createPortal(
    <div
      data-suggestion-popup
      style={{
        position: 'fixed',
        top: clientRect.bottom + POPUP_MARGIN,
        left: clientRect.left,
        zIndex: 50,
      }}
      className="max-h-72 w-72 overflow-y-auto rounded-md border border-neutral-200 bg-basic-white p-1 shadow-md"
    >
      {items.length === 0 ? (
        <div className="px-3 py-2 text-sm text-neutral-500">
          Brak pasujących zmiennych
        </div>
      ) : (
        items.map((item, i) => {
          const isBlock = isBlockVariable(item);
          const label = templateVariableDisplayNames[item.key] ?? item.key;
          const preview = isBlock
            ? '[tabela]'
            : item.exampleValue.length > 30
              ? `${item.exampleValue.slice(0, 30)}…`
              : item.exampleValue;

          return (
            <button
              key={item.key}
              type="button"
              onMouseEnter={() => setSelectedIndex(i)}
              onMouseDown={(e) => {
                // Prevent editor blur so the insert command runs against a
                // focused selection.
                e.preventDefault();
                select(i);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                i === selectedIndex
                  ? 'bg-accent-100'
                  : 'hover:bg-neutral-50',
              )}
            >
              {isBlock ? (
                <TableIcon size={12} className="shrink-0 text-neutral-500" />
              ) : (
                <Sparkles size={12} className="shrink-0 text-neutral-500" />
              )}
              <span className="truncate font-medium text-neutral-900">
                {label}
              </span>
              <span className="ml-auto truncate text-xs text-neutral-500">
                {preview}
              </span>
            </button>
          );
        })
      )}
    </div>,
    document.body,
  );
};

export default SuggestionPopup;
